import React, { useState, useEffect, useCallback } from 'react';
import ReactDOM from 'react-dom';
import { transportAPI } from '../services/api';
import { useYear } from '../context/YearContext';

// Renders children into document.body so parent CSS (transform, overflow:hidden,
// z-index stacking contexts) can never hide a modal.
function Modal({ children }) {
  if (typeof document === 'undefined') return null;
  return ReactDOM.createPortal(children, document.body);
}

// Small helpers
const btn = (variant = 'primary') => ({
  padding: '6px 12px', borderRadius: 6, fontSize: 13, fontWeight: 600,
  cursor: 'pointer', border: variant === 'primary' ? 'none' : '1.5px solid #cbd5e1',
  background: variant === 'primary' ? '#4f46e5' : variant === 'danger' ? '#fff' : '#fff',
  color: variant === 'primary' ? '#fff' : variant === 'danger' ? '#dc2626' : '#475569',
});
const input = {
  width: '100%', padding: '7px 10px', borderRadius: 6,
  border: '1.5px solid #e2e8f0', fontSize: 13, boxSizing: 'border-box',
};
const label = { fontSize: 11, fontWeight: 600, color: '#475569', display: 'block', marginBottom: 3 };

export default function TransportSection() {
  const [tab, setTab] = useState('buses'); // 'buses' | 'routes' | 'assign' | 'drivers'

  return (
    <div>
      <div style={{ display: 'flex', gap: 6, marginBottom: 18, borderBottom: '1px solid #e2e8f0', flexWrap: 'wrap' }}>
        {[
          ['buses', '🚌 Buses'],
          ['routes', '🛣 Routes & Stops'],
          ['assign', '👨‍🎓 Assign Students'],
          ['drivers', '👤 Drivers'],
        ].map(([k, l]) => (
          <button
            key={k}
            onClick={() => setTab(k)}
            style={{
              padding: '8px 16px', border: 'none', background: 'none', cursor: 'pointer',
              fontSize: 13, fontWeight: 600,
              color: tab === k ? '#4f46e5' : '#64748b',
              borderBottom: tab === k ? '2px solid #4f46e5' : '2px solid transparent',
              marginBottom: -1,
            }}
          >
            {l}
          </button>
        ))}

        <div style={{ marginLeft: 'auto', display: 'flex', alignItems: 'center' }}>
          <a
            href="/driver-login"
            target="_blank"
            rel="noopener noreferrer"
            style={{
              padding: '6px 12px', background: '#0b3d91', color: '#fff',
              borderRadius: 6, fontSize: 12, fontWeight: 700, textDecoration: 'none',
            }}
            title="Open driver-facing screen in a new tab (for training / testing)"
          >🚗 Driver Mode</a>
        </div>
      </div>

      {tab === 'buses'   && <BusesPanel />}
      {tab === 'routes'  && <RoutesPanel />}
      {tab === 'assign'  && <AssignPanel />}
      {tab === 'drivers' && <DriversPanel />}
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// BUSES
// ─────────────────────────────────────────────────────────────────────────────
function BusesPanel() {
  const [buses, setBuses] = useState([]);
  const [loading, setLoading] = useState(true);
  const [edit, setEdit] = useState(null);
  const [showForm, setShowForm] = useState(false);

  const load = useCallback(() => {
    setLoading(true);
    transportAPI.listBuses()
      .then(r => setBuses(r.data.data || []))
      .catch(() => setBuses([]))
      .finally(() => setLoading(false));
  }, []);
  useEffect(() => { load(); }, [load]);

  const del = async (b) => {
    if (!window.confirm(`Delete bus "${b.bus_number}"?\nRoutes using this bus will be unlinked.`)) return;
    try { await transportAPI.deleteBus(b.bus_id); load(); }
    catch (e) { alert(e.response?.data?.message || 'Failed to delete'); }
  };

  return (
    <div>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 14 }}>
        <div style={{ fontSize: 14, color: '#64748b' }}>{buses.length} bus{buses.length !== 1 ? 'es' : ''} registered</div>
        <button style={btn('primary')} onClick={() => { setEdit(null); setShowForm(true); }}>+ Add Bus</button>
      </div>

      {loading ? <div className="loading">Loading…</div> : buses.length === 0 ? (
        <div className="empty-state"><p>No buses yet. Click <strong>+ Add Bus</strong> to register the first vehicle.</p></div>
      ) : (
        <div className="table-container">
        <table className="data-table">
          <thead>
            <tr>
              <th>Bus No.</th><th>Registration</th><th>Capacity</th>
              <th>Driver</th><th>Conductor</th><th>Status</th><th>Actions</th>
            </tr>
          </thead>
          <tbody>
            {buses.map((b) => (
              <tr key={b.bus_id}>
                <td style={{ fontWeight: 700 }}>{b.bus_number}</td>
                <td style={{ fontFamily: 'monospace' }}>{b.registration_no || '—'}</td>
                <td>{b.capacity || '—'}</td>
                <td>
                  {b.driver_name || '—'}
                  {b.driver_phone && <div style={{ fontSize: 11, color: '#64748b' }}>📞 {b.driver_phone}</div>}
                </td>
                <td>
                  {b.conductor_name || '—'}
                  {b.conductor_phone && <div style={{ fontSize: 11, color: '#64748b' }}>📞 {b.conductor_phone}</div>}
                </td>
                <td>
                  <span style={{
                    padding: '2px 8px', borderRadius: 10, fontSize: 11, fontWeight: 600,
                    background: b.is_active ? '#dcfce7' : '#fee2e2',
                    color: b.is_active ? '#16a34a' : '#dc2626',
                  }}>{b.is_active ? 'Active' : 'Inactive'}</span>
                </td>
                <td>
                  <div style={{ display: 'flex', gap: 6 }}>
                    <button style={btn('secondary')} onClick={() => { setEdit(b); setShowForm(true); }}>Edit</button>
                    <button style={btn('danger')} onClick={() => del(b)}>Delete</button>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
        </div>
      )}

      {showForm && (
        <Modal>
          <BusForm bus={edit} onClose={() => setShowForm(false)} onSaved={() => { setShowForm(false); load(); }} />
        </Modal>
      )}
    </div>
  );
}

function BusForm({ bus, onClose, onSaved }) {
  const isEdit = !!bus?.bus_id;
  const [f, setF] = useState({
    bus_number:      bus?.bus_number      || '',
    registration_no: bus?.registration_no || '',
    capacity:        bus?.capacity        || '',
    driver_name:     bus?.driver_name     || '',
    driver_phone:    bus?.driver_phone    || '',
    conductor_name:  bus?.conductor_name  || '',
    conductor_phone: bus?.conductor_phone || '',
    is_active:       bus?.is_active !== false,
    notes:           bus?.notes || '',
  });
  const [saving, setSaving] = useState(false);
  const [err, setErr] = useState('');
  const set = (k, v) => setF({ ...f, [k]: v });

  const save = async () => {
    if (!f.bus_number.trim()) { setErr('Bus number is required'); return; }
    setSaving(true); setErr('');
    try {
      const payload = { ...f, capacity: f.capacity ? parseInt(f.capacity, 10) : null };
      if (isEdit) await transportAPI.updateBus(bus.bus_id, payload);
      else await transportAPI.createBus(payload);
      onSaved();
    } catch (e) { setErr(e.response?.data?.message || 'Failed to save'); }
    finally { setSaving(false); }
  };

  return (
    <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.45)', zIndex: 1000, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 16 }}>
      <div style={{ background: '#fff', borderRadius: 12, width: '100%', maxWidth: 520, maxHeight: '90vh', overflowY: 'auto' }}>
        <div style={{ padding: '14px 20px', borderBottom: '1px solid #f1f5f9', fontWeight: 700 }}>
          {isEdit ? 'Edit Bus' : 'Add Bus'}
        </div>
        <div style={{ padding: 20, display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
          <div style={{ gridColumn: '1 / -1' }}>
            <label style={label}>Bus Number / Name *</label>
            <input style={input} value={f.bus_number} onChange={e => set('bus_number', e.target.value)} placeholder="e.g. Bus 1 or Route A" />
          </div>
          <div>
            <label style={label}>Registration No.</label>
            <input style={input} value={f.registration_no} onChange={e => set('registration_no', e.target.value)} placeholder="AP09 CD 1234" />
          </div>
          <div>
            <label style={label}>Capacity</label>
            <input style={input} type="number" value={f.capacity} onChange={e => set('capacity', e.target.value)} placeholder="40" />
          </div>
          <div>
            <label style={label}>Driver Name</label>
            <input style={input} value={f.driver_name} onChange={e => set('driver_name', e.target.value)} />
          </div>
          <div>
            <label style={label}>Driver Phone</label>
            <input style={input} value={f.driver_phone} onChange={e => set('driver_phone', e.target.value.replace(/[^\d]/g, ''))} maxLength={15} />
          </div>
          <div>
            <label style={label}>Conductor Name</label>
            <input style={input} value={f.conductor_name} onChange={e => set('conductor_name', e.target.value)} />
          </div>
          <div>
            <label style={label}>Conductor Phone</label>
            <input style={input} value={f.conductor_phone} onChange={e => set('conductor_phone', e.target.value.replace(/[^\d]/g, ''))} maxLength={15} />
          </div>
          <div style={{ gridColumn: '1 / -1' }}>
            <label style={label}>Notes</label>
            <input style={input} value={f.notes} onChange={e => set('notes', e.target.value)} />
          </div>
          {isEdit && (
            <div style={{ gridColumn: '1 / -1', display: 'flex', alignItems: 'center', gap: 8 }}>
              <input type="checkbox" checked={f.is_active} onChange={e => set('is_active', e.target.checked)} id="bus_active" />
              <label htmlFor="bus_active" style={{ fontSize: 13, cursor: 'pointer' }}>Active</label>
            </div>
          )}
          {err && <div style={{ gridColumn: '1 / -1', color: '#dc2626', fontSize: 12 }}>{err}</div>}
        </div>
        <div style={{ padding: '12px 20px', borderTop: '1px solid #f1f5f9', display: 'flex', gap: 8, justifyContent: 'flex-end' }}>
          <button style={btn('secondary')} onClick={onClose}>Cancel</button>
          <button style={btn('primary')} onClick={save} disabled={saving}>{saving ? 'Saving…' : (isEdit ? 'Update' : 'Add Bus')}</button>
        </div>
      </div>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// ROUTES & STOPS
// ─────────────────────────────────────────────────────────────────────────────
function RoutesPanel() {
  const [routes, setRoutes] = useState([]);
  const [buses, setBuses] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [edit, setEdit] = useState(null);
  const [openRoute, setOpenRoute] = useState(null); // route_id whose stops are open

  const load = useCallback(() => {
    setLoading(true);
    Promise.all([transportAPI.listRoutes(), transportAPI.listBuses()])
      .then(([r, b]) => { setRoutes(r.data.data || []); setBuses(b.data.data || []); })
      .catch(() => {})
      .finally(() => setLoading(false));
  }, []);
  useEffect(() => { load(); }, [load]);

  const del = async (r) => {
    if (!window.confirm(`Delete route "${r.route_name}"?\nAll stops and student assignments on this route will be cleared.`)) return;
    try { await transportAPI.deleteRoute(r.route_id); if (openRoute === r.route_id) setOpenRoute(null); load(); }
    catch (e) { alert(e.response?.data?.message || 'Failed to delete'); }
  };

  const printSheet = async (routeId) => {
    try {
      const res = await transportAPI.getRouteSheet(routeId);
      const d = res.data.data;
      const win = window.open('', '_blank');
      if (!win) { alert('Popup blocked'); return; }
      const stopsHtml = d.stops.map(s => `
        <tr><td colspan="4" style="background:#eef2ff;padding:8px;font-weight:700">
          Stop ${s.sequence_no}: ${escapeHtml(s.stop_name)}
          ${s.pickup_time ? ` <span style="color:#64748b;font-weight:400">· Pickup ${s.pickup_time}</span>` : ''}
          ${s.drop_time ? ` <span style="color:#64748b;font-weight:400">· Drop ${s.drop_time}</span>` : ''}
        </td></tr>
        ${(s.students || []).length === 0
          ? '<tr><td colspan="4" style="padding:6px 12px;color:#94a3b8;font-size:12px">No students assigned</td></tr>'
          : s.students.map((st, i) => `
            <tr>
              <td style="padding:6px 8px;width:30px;color:#94a3b8">${i+1}</td>
              <td style="padding:6px 8px"><strong>${escapeHtml(st.student_name)}</strong></td>
              <td style="padding:6px 8px">${escapeHtml(st.class || '')}</td>
              <td style="padding:6px 8px;font-family:monospace">${escapeHtml(st.mobile_number || '')}</td>
            </tr>`).join('')}
      `).join('');
      const r = d.route;
      win.document.write(`
        <html><head><title>Route Sheet – ${escapeHtml(r.route_name)}</title>
        <style>
          body{font-family:Arial,sans-serif;padding:20px;color:#1e293b}
          h1{margin:0}
          .meta{color:#64748b;font-size:13px;margin:6px 0 18px}
          .card{border:1px solid #cbd5e1;border-radius:8px;padding:12px 16px;margin-bottom:16px;background:#f8fafc}
          table{width:100%;border-collapse:collapse;font-size:12px;margin-top:8px}
          td{border-bottom:1px solid #e2e8f0}
          @media print { button{display:none} .card{background:#fff} }
        </style></head><body>
        <button onclick="window.print()" style="float:right;padding:8px 14px;cursor:pointer">🖨 Print</button>
        <h1>${escapeHtml(r.route_name)}</h1>
        <div class="meta">Route Sheet · ${d.total_students} student(s) assigned</div>
        <div class="card">
          <div><b>Bus:</b> ${escapeHtml(r.bus_number || 'Not assigned')} ${r.registration_no ? '· ' + escapeHtml(r.registration_no) : ''}</div>
          <div><b>Driver:</b> ${escapeHtml(r.driver_name || '—')} ${r.driver_phone ? '· 📞 ' + escapeHtml(r.driver_phone) : ''}</div>
          ${r.conductor_name ? `<div><b>Conductor:</b> ${escapeHtml(r.conductor_name)} ${r.conductor_phone ? '· 📞 ' + escapeHtml(r.conductor_phone) : ''}</div>` : ''}
          <div><b>Timings:</b> Morning ${escapeHtml(r.morning_start || '—')} · Evening ${escapeHtml(r.evening_start || '—')}</div>
          ${r.start_point || r.end_point ? `<div><b>Route:</b> ${escapeHtml(r.start_point || '—')} → ${escapeHtml(r.end_point || '—')}</div>` : ''}
        </div>
        <table>${stopsHtml}</table>
        ${d.unassigned_students.length ? `
          <div style="margin-top:20px">
            <div style="font-weight:700;color:#dc2626;margin-bottom:6px">⚠ ${d.unassigned_students.length} student(s) on this route with no stop assigned:</div>
            <ul>${d.unassigned_students.map(s => `<li>${escapeHtml(s.student_name)} (${escapeHtml(s.class || '')})</li>`).join('')}</ul>
          </div>` : ''}
        </body></html>
      `);
      win.document.close();
    } catch (e) { alert(e.response?.data?.message || 'Failed to load route sheet'); }
  };

  return (
    <div>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 14 }}>
        <div style={{ fontSize: 14, color: '#64748b' }}>{routes.length} route{routes.length !== 1 ? 's' : ''}</div>
        <button
          style={btn('primary')}
          onClick={() => {
            try {
              console.log('[transport] + Add Route clicked, buses loaded:', buses.length);
              setEdit(null);
              setShowForm(true);
            } catch (err) {
              console.error('[transport] Add Route click failed:', err);
              alert('Could not open Add Route dialog: ' + err.message);
            }
          }}
        >+ Add Route</button>
      </div>

      {loading ? <div className="loading">Loading…</div> : routes.length === 0 ? (
        <div className="empty-state"><p>No routes yet. Add a bus first, then create a route.</p></div>
      ) : (
        <div className="table-container">
        <table className="data-table">
          <thead>
            <tr>
              <th>Route Name</th><th>Bus</th><th>Timings</th>
              <th style={{ textAlign: 'center' }}>Stops</th>
              <th style={{ textAlign: 'center' }}>Students</th>
              <th>Actions</th>
            </tr>
          </thead>
          <tbody>
            {routes.map(r => (
              <React.Fragment key={r.route_id}>
                <tr>
                  <td>
                    <div style={{ fontWeight: 700 }}>{r.route_name}</div>
                    {(r.start_point || r.end_point) && (
                      <div style={{ fontSize: 11, color: '#94a3b8' }}>
                        {r.start_point || '?'} → {r.end_point || '?'}
                      </div>
                    )}
                  </td>
                  <td>{r.bus_number || <span style={{ color: '#dc2626', fontSize: 12 }}>Not assigned</span>}</td>
                  <td style={{ fontSize: 12 }}>
                    {r.morning_start && <div>🌅 {r.morning_start}</div>}
                    {r.evening_start && <div>🌇 {r.evening_start}</div>}
                    {!r.morning_start && !r.evening_start && '—'}
                  </td>
                  <td style={{ textAlign: 'center', fontWeight: 700 }}>{r.stops_count}</td>
                  <td style={{ textAlign: 'center', fontWeight: 700, color: '#4f46e5' }}>{r.students_count}</td>
                  <td>
                    <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
                      <button style={btn('secondary')} onClick={() => setOpenRoute(openRoute === r.route_id ? null : r.route_id)}>
                        {openRoute === r.route_id ? '▲ Close' : '▼ Stops'}
                      </button>
                      <button style={btn('secondary')} onClick={() => printSheet(r.route_id)}>🖨 Print</button>
                      <button style={btn('secondary')} onClick={() => { setEdit(r); setShowForm(true); }}>Edit</button>
                      <button style={btn('danger')} onClick={() => del(r)}>Delete</button>
                    </div>
                  </td>
                </tr>
                {openRoute === r.route_id && (
                  <tr>
                    <td colSpan={6} style={{ background: '#f8fafc', padding: 0 }}>
                      <StopsEditor routeId={r.route_id} onChanged={load} />
                    </td>
                  </tr>
                )}
              </React.Fragment>
            ))}
          </tbody>
        </table>
        </div>
      )}

      {showForm && (
        <Modal>
          <RouteForm route={edit} buses={buses} onClose={() => setShowForm(false)} onSaved={() => { setShowForm(false); load(); }} />
        </Modal>
      )}
    </div>
  );
}

function RouteForm({ route, buses, onClose, onSaved }) {
  const { year } = useYear();
  const isEdit = !!route?.route_id;
  const [f, setF] = useState({
    route_name:    route?.route_name    || '',
    bus_id:        route?.bus_id        || '',
    start_point:   route?.start_point   || '',
    end_point:     route?.end_point     || '',
    morning_start: route?.morning_start || '',
    evening_start: route?.evening_start || '',
    notes:         route?.notes         || '',
    is_active:     route?.is_active !== false,
  });
  const [saving, setSaving] = useState(false);
  const [err, setErr] = useState('');
  const [villages, setVillages] = useState([]);
  const set = (k, v) => setF({ ...f, [k]: v });

  // Load villages from Operations → Transport Fee Template
  useEffect(() => {
    if (!year) return;
    transportAPI.listVillages(year)
      .then(r => setVillages(r.data.data || []))
      .catch(() => setVillages([]));
  }, [year]);

  const save = async () => {
    if (!f.route_name.trim()) { setErr('Route name is required'); return; }
    setSaving(true); setErr('');
    try {
      const payload = { ...f, bus_id: f.bus_id ? parseInt(f.bus_id, 10) : null };
      if (isEdit) await transportAPI.updateRoute(route.route_id, payload);
      else await transportAPI.createRoute(payload);
      onSaved();
    } catch (e) { setErr(e.response?.data?.message || 'Failed to save'); }
    finally { setSaving(false); }
  };

  return (
    <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.45)', zIndex: 1000, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 16 }}>
      <div style={{ background: '#fff', borderRadius: 12, width: '100%', maxWidth: 520, maxHeight: '90vh', overflowY: 'auto' }}>
        <div style={{ padding: '14px 20px', borderBottom: '1px solid #f1f5f9', fontWeight: 700 }}>
          {isEdit ? 'Edit Route' : 'Add Route'}
        </div>
        <div style={{ padding: 20, display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
          <div style={{ gridColumn: '1 / -1' }}>
            <label style={label}>Route Name (Village) *</label>
            {villages.length === 0 ? (
              <>
                <div style={{ padding: '8px 10px', border: '1.5px dashed #fca5a5', borderRadius: 6, background: '#fef2f2', color: '#dc2626', fontSize: 12, marginBottom: 6 }}>
                  No villages defined yet. Please add villages in Operations → Transport Fee Template first.
                </div>
                <input style={input} value={f.route_name} onChange={e => set('route_name', e.target.value)} placeholder="Type route name manually" />
              </>
            ) : (
              <select style={input} value={f.route_name} onChange={e => set('route_name', e.target.value)}>
                <option value="">-- Select village --</option>
                {villages.map(v => (
                  <option key={v.village_name} value={v.village_name}>{v.village_name}</option>
                ))}
                {isEdit && f.route_name && !villages.some(v => v.village_name === f.route_name) && (
                  <option value={f.route_name}>{f.route_name} (existing)</option>
                )}
              </select>
            )}
            {villages.length > 0 && (
              <div style={{ fontSize: 10, color: '#94a3b8', marginTop: 3 }}>
                💡 Villages come from Operations → Transport Fee Template
              </div>
            )}
          </div>
          <div style={{ gridColumn: '1 / -1' }}>
            <label style={label}>Bus</label>
            <select style={input} value={f.bus_id} onChange={e => set('bus_id', e.target.value)}>
              <option value="">-- Select bus --</option>
              {buses.filter(b => b.is_active).map(b => (
                <option key={b.bus_id} value={b.bus_id}>{b.bus_number}{b.registration_no ? ` · ${b.registration_no}` : ''}</option>
              ))}
            </select>
          </div>
          <div>
            <label style={label}>Start Point</label>
            <input style={input} value={f.start_point} onChange={e => set('start_point', e.target.value)} />
          </div>
          <div>
            <label style={label}>End Point</label>
            <input style={input} value={f.end_point} onChange={e => set('end_point', e.target.value)} />
          </div>
          <div>
            <label style={label}>Morning Start Time</label>
            <input style={input} type="time" value={f.morning_start} onChange={e => set('morning_start', e.target.value)} />
          </div>
          <div>
            <label style={label}>Evening Start Time</label>
            <input style={input} type="time" value={f.evening_start} onChange={e => set('evening_start', e.target.value)} />
          </div>
          <div style={{ gridColumn: '1 / -1' }}>
            <label style={label}>Notes</label>
            <input style={input} value={f.notes} onChange={e => set('notes', e.target.value)} />
          </div>
          {isEdit && (
            <div style={{ gridColumn: '1 / -1', display: 'flex', alignItems: 'center', gap: 8 }}>
              <input type="checkbox" checked={f.is_active} onChange={e => set('is_active', e.target.checked)} id="route_active" />
              <label htmlFor="route_active" style={{ fontSize: 13, cursor: 'pointer' }}>Active</label>
            </div>
          )}
          {err && <div style={{ gridColumn: '1 / -1', color: '#dc2626', fontSize: 12 }}>{err}</div>}
        </div>
        <div style={{ padding: '12px 20px', borderTop: '1px solid #f1f5f9', display: 'flex', gap: 8, justifyContent: 'flex-end' }}>
          <button style={btn('secondary')} onClick={onClose}>Cancel</button>
          <button style={btn('primary')} onClick={save} disabled={saving}>{saving ? 'Saving…' : (isEdit ? 'Update' : 'Add Route')}</button>
        </div>
      </div>
    </div>
  );
}

function StopsEditor({ routeId, onChanged }) {
  const { year } = useYear();
  const [stops, setStops] = useState([]);
  const [loading, setLoading] = useState(true);
  const [newStop, setNewStop] = useState({ stop_name: '', pickup_time: '', drop_time: '' });
  const [saving, setSaving] = useState(false);
  const [villages, setVillages] = useState([]);

  const load = useCallback(() => {
    setLoading(true);
    transportAPI.listStops(routeId)
      .then(r => setStops(r.data.data || []))
      .catch(() => setStops([]))
      .finally(() => setLoading(false));
  }, [routeId]);
  useEffect(() => { load(); }, [load]);

  // Load villages from Operations → Transport Fee Template
  useEffect(() => {
    if (!year) return;
    transportAPI.listVillages(year)
      .then(r => setVillages(r.data.data || []))
      .catch(() => setVillages([]));
  }, [year]);

  const add = async () => {
    if (!newStop.stop_name.trim()) return;
    setSaving(true);
    try {
      await transportAPI.createStop(routeId, newStop);
      setNewStop({ stop_name: '', pickup_time: '', drop_time: '' });
      load(); onChanged?.();
    } catch (e) { alert(e.response?.data?.message || 'Failed to add'); }
    finally { setSaving(false); }
  };

  const del = async (s) => {
    if (!window.confirm(`Delete stop "${s.stop_name}"?\nStudents at this stop will have their stop cleared (route remains).`)) return;
    try { await transportAPI.deleteStop(s.stop_id); load(); onChanged?.(); }
    catch (e) { alert(e.response?.data?.message || 'Failed to delete'); }
  };

  const updateSeq = async (s, delta) => {
    const newSeq = Math.max(1, s.sequence_no + delta);
    try { await transportAPI.updateStop(s.stop_id, { sequence_no: newSeq }); load(); }
    catch (e) { alert('Failed to reorder'); }
  };

  const existingLower = new Set(stops.map(s => s.stop_name.toLowerCase()));
  const villagesAvailable = villages.filter(v => !existingLower.has((v.village_name || '').toLowerCase()));

  return (
    <div style={{ padding: 16 }}>
      <div style={{ fontWeight: 700, color: '#1e293b', marginBottom: 10 }}>Stops on this route</div>
      {loading ? <div className="loading">Loading…</div> : (
        <>
          {stops.length === 0 ? (
            <div style={{ padding: 12, color: '#94a3b8', fontStyle: 'italic', fontSize: 13 }}>No stops yet.</div>
          ) : (
            <div className="table-container">
            <table style={{ width: '100%', fontSize: 13, borderCollapse: 'collapse' }}>
              <thead>
                <tr style={{ background: '#eef2ff' }}>
                  <th style={{ padding: '6px 8px', textAlign: 'left', width: 60 }}>Order</th>
                  <th style={{ padding: '6px 8px', textAlign: 'left' }}>Stop Name</th>
                  <th style={{ padding: '6px 8px', textAlign: 'center', width: 100 }}>Pickup</th>
                  <th style={{ padding: '6px 8px', textAlign: 'center', width: 100 }}>Drop</th>
                  <th style={{ padding: '6px 8px', textAlign: 'center', width: 160 }}>Actions</th>
                </tr>
              </thead>
              <tbody>
                {stops.map((s, i) => (
                  <tr key={s.stop_id} style={{ borderBottom: '1px solid #e2e8f0' }}>
                    <td style={{ padding: '6px 8px', fontWeight: 700 }}>{s.sequence_no}</td>
                    <td style={{ padding: '6px 8px' }}>{s.stop_name}</td>
                    <td style={{ padding: '6px 8px', textAlign: 'center' }}>{s.pickup_time || '—'}</td>
                    <td style={{ padding: '6px 8px', textAlign: 'center' }}>{s.drop_time || '—'}</td>
                    <td style={{ padding: '6px 8px', textAlign: 'center' }}>
                      <div style={{ display: 'inline-flex', gap: 4 }}>
                        <button style={{ ...btn('secondary'), padding: '3px 8px' }} onClick={() => updateSeq(s, -1)} disabled={i === 0}>↑</button>
                        <button style={{ ...btn('secondary'), padding: '3px 8px' }} onClick={() => updateSeq(s, 1)} disabled={i === stops.length - 1}>↓</button>
                        <button style={{ ...btn('danger'), padding: '3px 8px' }} onClick={() => del(s)}>×</button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
            </div>
          )}
          <div style={{ marginTop: 14, padding: 12, background: '#fff', borderRadius: 8, border: '1px dashed #cbd5e1', display: 'flex', gap: 8, alignItems: 'flex-end', flexWrap: 'wrap' }}>
            <div style={{ flex: '2 1 200px' }}>
              <label style={label}>Village (Stop) *</label>
              {villages.length === 0 ? (
                <div style={{ padding: '8px 10px', border: '1.5px dashed #fca5a5', borderRadius: 6, background: '#fef2f2', color: '#dc2626', fontSize: 12 }}>
                  No villages defined yet. Please add villages in Operations → Transport Fee Template first.
                </div>
              ) : (
                <select
                  style={input}
                  value={newStop.stop_name}
                  onChange={e => setNewStop({ ...newStop, stop_name: e.target.value })}
                >
                  <option value="">-- Select village --</option>
                  {villagesAvailable.map(v => (
                    <option key={v.village_name} value={v.village_name}>{v.village_name}</option>
                  ))}
                </select>
              )}
              {villages.length > 0 && villagesAvailable.length === 0 && (
                <div style={{ fontSize: 11, color: '#94a3b8', marginTop: 3 }}>
                  All {villages.length} villages are already added as stops on this route.
                </div>
              )}
            </div>
            <div style={{ flex: '1 1 100px' }}>
              <label style={label}>Pickup</label>
              <input style={input} type="time" value={newStop.pickup_time} onChange={e => setNewStop({ ...newStop, pickup_time: e.target.value })} />
            </div>
            <div style={{ flex: '1 1 100px' }}>
              <label style={label}>Drop</label>
              <input style={input} type="time" value={newStop.drop_time} onChange={e => setNewStop({ ...newStop, drop_time: e.target.value })} />
            </div>
            <button style={btn('primary')} onClick={add} disabled={saving || !newStop.stop_name.trim()}>+ Add Stop</button>
          </div>
        </>
      )}
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// STUDENT ASSIGNMENT
// ─────────────────────────────────────────────────────────────────────────────
function AssignPanel() {
  const [routes, setRoutes] = useState([]);
  const [stopsByRoute, setStopsByRoute] = useState({}); // { routeId: [stops] }
  const [students, setStudents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState(''); // '' | 'assigned' | 'unassigned' | routeId
  const [search, setSearch] = useState('');
  const [savingIds, setSavingIds] = useState(new Set());

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [rRes, aRes] = await Promise.all([
        transportAPI.listRoutes(),
        transportAPI.listAssignments(),
      ]);
      setRoutes(rRes.data.data || []);
      setStudents(aRes.data.data || []);
      // Preload stops for all routes
      const stopsMap = {};
      await Promise.all((rRes.data.data || []).map(async (r) => {
        try {
          const s = await transportAPI.listStops(r.route_id);
          stopsMap[r.route_id] = s.data.data || [];
        } catch { stopsMap[r.route_id] = []; }
      }));
      setStopsByRoute(stopsMap);
    } catch { /* ignore */ }
    finally { setLoading(false); }
  }, []);
  useEffect(() => { load(); }, [load]);

  const updateOne = async (student, routeId, stopId) => {
    setSavingIds(prev => new Set(prev).add(student.student_id));
    try {
      if (!routeId) {
        await transportAPI.clearAssignment(student.student_id);
      } else {
        await transportAPI.assignStudent(student.student_id, {
          bus_route_id: routeId,
          bus_stop_id: stopId || null,
        });
      }
      setStudents(prev => prev.map(s => s.student_id === student.student_id
        ? { ...s, bus_route_id: routeId || null, bus_stop_id: stopId || null,
            route_name: routes.find(r => r.route_id === Number(routeId))?.route_name || null,
            stop_name: stopsByRoute[routeId]?.find(x => x.stop_id === Number(stopId))?.stop_name || null }
        : s));
    } catch (e) { alert(e.response?.data?.message || 'Failed to save'); }
    finally {
      setSavingIds(prev => { const n = new Set(prev); n.delete(student.student_id); return n; });
    }
  };

  const filtered = students.filter(s => {
    if (search) {
      const q = search.toLowerCase();
      if (!s.student_name.toLowerCase().includes(q) &&
          !(s.class || '').toLowerCase().includes(q) &&
          !(s.father_name || '').toLowerCase().includes(q)) return false;
    }
    if (filter === 'assigned' && !s.bus_route_id) return false;
    if (filter === 'unassigned' && s.bus_route_id) return false;
    if (filter && filter !== 'assigned' && filter !== 'unassigned') {
      if (String(s.bus_route_id) !== String(filter)) return false;
    }
    return true;
  });

  const assignedCount = students.filter(s => s.bus_route_id).length;

  return (
    <div>
      <div style={{ display: 'flex', gap: 10, marginBottom: 14, flexWrap: 'wrap', alignItems: 'center' }}>
        <input
          style={{ ...input, maxWidth: 260 }}
          value={search} onChange={e => setSearch(e.target.value)}
          placeholder="Search by name, class, father…"
        />
        <select style={{ ...input, maxWidth: 220 }} value={filter} onChange={e => setFilter(e.target.value)}>
          <option value="">All students</option>
          <option value="assigned">Assigned only</option>
          <option value="unassigned">Unassigned only</option>
          <option disabled>──────</option>
          {routes.map(r => <option key={r.route_id} value={r.route_id}>Route: {r.route_name}</option>)}
        </select>
        <div style={{ marginLeft: 'auto', fontSize: 13, color: '#64748b' }}>
          {assignedCount} of {students.length} students on a route
        </div>
      </div>

      {loading ? <div className="loading">Loading…</div> : filtered.length === 0 ? (
        <div className="empty-state"><p>No students match your filter.</p></div>
      ) : (
        <div className="table-container">
        <table className="data-table">
          <thead>
            <tr>
              <th style={{ width: 40 }}>#</th>
              <th>Student</th>
              <th>Class</th>
              <th>Route</th>
              <th>Stop</th>
            </tr>
          </thead>
          <tbody>
            {filtered.map((s, i) => {
              const rid = s.bus_route_id ? Number(s.bus_route_id) : '';
              const availStops = stopsByRoute[rid] || [];
              const isSaving = savingIds.has(s.student_id);
              return (
                <tr key={s.student_id}>
                  <td>{i + 1}</td>
                  <td>
                    <div style={{ fontWeight: 600 }}>{s.student_name}</div>
                    {s.father_name && <div style={{ fontSize: 11, color: '#94a3b8' }}>Father: {s.father_name}</div>}
                  </td>
                  <td>{s.class || '—'}</td>
                  <td>
                    <select
                      style={{ ...input, minWidth: 180, opacity: isSaving ? 0.6 : 1 }}
                      value={rid}
                      disabled={isSaving}
                      onChange={e => updateOne(s, e.target.value ? Number(e.target.value) : null, null)}
                    >
                      <option value="">— None —</option>
                      {routes.map(r => <option key={r.route_id} value={r.route_id}>{r.route_name}</option>)}
                    </select>
                  </td>
                  <td>
                    <select
                      style={{ ...input, minWidth: 160, opacity: (isSaving || !rid) ? 0.6 : 1 }}
                      value={s.bus_stop_id || ''}
                      disabled={isSaving || !rid}
                      onChange={e => updateOne(s, rid, e.target.value ? Number(e.target.value) : null)}
                    >
                      <option value="">— Any / not set —</option>
                      {availStops.map(st => <option key={st.stop_id} value={st.stop_id}>{st.sequence_no}. {st.stop_name}</option>)}
                    </select>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
        </div>
      )}
    </div>
  );
}

function escapeHtml(s) {
  if (s == null) return '';
  return String(s).replace(/[&<>"']/g, c => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[c]));
}

// ─────────────────────────────────────────────────────────────────────────────
// DRIVERS (with PIN for driver-mode login)
// ─────────────────────────────────────────────────────────────────────────────
function DriversPanel() {
  const [drivers, setDrivers] = useState([]);
  const [buses, setBuses]     = useState([]);
  const [loading, setLoading] = useState(true);
  const [edit, setEdit]       = useState(null);
  const [showForm, setShowForm] = useState(false);
  const [pinFor, setPinFor]   = useState(null);

  const load = useCallback(() => {
    setLoading(true);
    Promise.all([
      transportAPI.listDrivers().catch(() => ({ data: { data: [] } })),
      transportAPI.listBuses().catch(()   => ({ data: { data: [] } })),
    ]).then(([d, b]) => {
      setDrivers(d.data.data || []);
      setBuses(b.data.data   || []);
    }).finally(() => setLoading(false));
  }, []);
  useEffect(() => { load(); }, [load]);

  const del = async (d) => {
    if (!window.confirm(`Delete driver "${d.name}"?`)) return;
    try { await transportAPI.deleteDriver(d.driver_id); load(); }
    catch (e) { alert(e?.response?.data?.message || 'Delete failed'); }
  };
  const toggleActive = async (d) => {
    try { await transportAPI.updateDriver(d.driver_id, { is_active: !d.is_active }); load(); }
    catch (e) { alert(e?.response?.data?.message || 'Failed'); }
  };

  return (
    <div>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 12 }}>
        <h3 style={{ margin: 0 }}>Drivers</h3>
        <button style={btn('primary')} onClick={() => { setEdit(null); setShowForm(true); }}>+ Add Driver</button>
      </div>

      {loading ? <div>Loading…</div> : drivers.length === 0 ? (
        <div style={{ padding: 20, color: '#64748b', textAlign: 'center' }}>
          No drivers yet. Add one to enable driver-app login.
        </div>
      ) : (
        <div className="table-container">
        <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
          <thead>
            <tr style={{ background: '#f1f5f9', textAlign: 'left' }}>
              <th style={th}>Name</th>
              <th style={th}>Phone</th>
              <th style={th}>Assigned Bus</th>
              <th style={th}>PIN</th>
              <th style={th}>Status</th>
              <th style={{ ...th, textAlign: 'right' }}>Actions</th>
            </tr>
          </thead>
          <tbody>
            {drivers.map((d, i) => (
              <tr key={d.driver_id} style={{ background: i % 2 ? '#fafbff' : '#fff' }}>
                <td style={td}><strong>{d.name}</strong></td>
                <td style={td}>{d.phone || '—'}</td>
                <td style={td}>{d.bus_number || <em style={{ color: '#94a3b8' }}>(none)</em>}</td>
                <td style={td}>
                  {d.has_pin
                    ? <span style={{ color: '#059669', fontWeight: 700 }}>✓ Set</span>
                    : <span style={{ color: '#dc2626', fontWeight: 700 }}>✗ Not set</span>}
                </td>
                <td style={td}>
                  <button
                    onClick={() => toggleActive(d)}
                    style={{
                      padding: '2px 10px', fontSize: 11, fontWeight: 700, borderRadius: 4,
                      border: '1px solid',
                      background: d.is_active ? '#dcfce7' : '#fee2e2',
                      color:      d.is_active ? '#166534' : '#991b1b',
                      borderColor: d.is_active ? '#bbf7d0' : '#fecaca',
                      cursor: 'pointer',
                    }}
                    title="Click to toggle"
                  >{d.is_active ? 'ACTIVE' : 'INACTIVE'}</button>
                </td>
                <td style={{ ...td, textAlign: 'right', whiteSpace: 'nowrap' }}>
                  <button style={{ ...btn('secondary'), marginRight: 4 }}
                    onClick={() => setPinFor(d)}>🔑 Set PIN</button>
                  <button style={{ ...btn('secondary'), marginRight: 4 }}
                    onClick={() => { setEdit(d); setShowForm(true); }}>Edit</button>
                  <button style={btn('danger')} onClick={() => del(d)}>Delete</button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
        </div>
      )}

      {showForm && (
        <DriverFormModal
          initial={edit}
          buses={buses}
          onClose={() => { setShowForm(false); setEdit(null); }}
          onSaved={() => { setShowForm(false); setEdit(null); load(); }}
        />
      )}
      {pinFor && (
        <SetPinModal
          driver={pinFor}
          onClose={() => setPinFor(null)}
          onSaved={() => { setPinFor(null); load(); }}
        />
      )}
    </div>
  );
}

const th = { padding: '10px 12px', fontSize: 12, fontWeight: 700, color: '#475569', borderBottom: '1px solid #e2e8f0' };
const td = { padding: '10px 12px', borderBottom: '1px solid #f1f5f9' };

function DriverFormModal({ initial, buses, onClose, onSaved }) {
  const [name, setName]     = useState(initial?.name || '');
  const [phone, setPhone]   = useState(initial?.phone || '');
  const [busId, setBusId]   = useState(initial?.bus_id || '');
  const [pin, setPin]       = useState('');
  const [busy, setBusy]     = useState(false);
  const [err, setErr]       = useState('');

  const save = async () => {
    setErr('');
    if (!name.trim()) { setErr('Name is required'); return; }
    if (pin && pin.length < 4) { setErr('PIN must be at least 4 digits'); return; }
    setBusy(true);
    try {
      const body = {
        name: name.trim(),
        phone: phone.trim() || null,
        bus_id: busId ? parseInt(busId, 10) : null,
      };
      if (pin) body.pin = pin.trim();
      if (initial?.driver_id) await transportAPI.updateDriver(initial.driver_id, body);
      else                    await transportAPI.createDriver(body);
      onSaved();
    } catch (e) {
      setErr(e?.response?.data?.message || 'Save failed');
      setBusy(false);
    }
  };

  return (
    <Modal>
      <div style={modalBackdrop} onClick={onClose}>
        <div style={modalBox} onClick={e => e.stopPropagation()}>
          <h3 style={{ margin: '0 0 14px' }}>{initial ? 'Edit Driver' : 'Add Driver'}</h3>

          <div style={{ marginBottom: 10 }}>
            <label style={label}>Name *</label>
            <input style={input} value={name} onChange={e => setName(e.target.value)} />
          </div>
          <div style={{ marginBottom: 10 }}>
            <label style={label}>Phone</label>
            <input style={input} value={phone} onChange={e => setPhone(e.target.value)} placeholder="10-digit mobile" />
          </div>
          <div style={{ marginBottom: 10 }}>
            <label style={label}>Assigned Bus</label>
            <select style={input} value={busId} onChange={e => setBusId(e.target.value)}>
              <option value="">— None —</option>
              {buses.map(b => (
                <option key={b.bus_id} value={b.bus_id}>
                  {b.bus_number}{b.registration_no ? ` (${b.registration_no})` : ''}
                </option>
              ))}
            </select>
          </div>
          <div style={{ marginBottom: 10 }}>
            <label style={label}>{initial ? 'New PIN (leave blank to keep current)' : 'PIN (optional — set later)'}</label>
            <input
              style={input}
              value={pin}
              onChange={e => setPin(e.target.value.replace(/\D/g, '').slice(0, 8))}
              inputMode="numeric"
              placeholder="4-8 digits"
              type="text"
            />
          </div>

          {err && <div style={{ background: '#fee', color: '#b00', padding: 8, borderRadius: 6, fontSize: 12, marginBottom: 10 }}>{err}</div>}

          <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8 }}>
            <button style={btn('secondary')} onClick={onClose} disabled={busy}>Cancel</button>
            <button style={btn('primary')} onClick={save} disabled={busy}>{busy ? 'Saving…' : 'Save'}</button>
          </div>
        </div>
      </div>
    </Modal>
  );
}

function SetPinModal({ driver, onClose, onSaved }) {
  const [pin, setPin]   = useState('');
  const [busy, setBusy] = useState(false);
  const [err, setErr]   = useState('');

  const save = async () => {
    setErr('');
    if (pin && pin.length < 4) { setErr('PIN must be at least 4 digits'); return; }
    setBusy(true);
    try {
      await transportAPI.updateDriver(driver.driver_id, { pin });
      onSaved();
    } catch (e) {
      setErr(e?.response?.data?.message || 'Save failed');
      setBusy(false);
    }
  };
  const clearPin = async () => {
    if (!window.confirm('Clear PIN? Driver will not be able to log in.')) return;
    setBusy(true);
    try { await transportAPI.updateDriver(driver.driver_id, { pin: '' }); onSaved(); }
    catch (e) { setErr(e?.response?.data?.message || 'Failed'); setBusy(false); }
  };

  return (
    <Modal>
      <div style={modalBackdrop} onClick={onClose}>
        <div style={modalBox} onClick={e => e.stopPropagation()}>
          <h3 style={{ margin: '0 0 6px' }}>🔑 Set PIN — {driver.name}</h3>
          <p style={{ margin: '0 0 14px', color: '#64748b', fontSize: 13 }}>
            The driver will enter <strong>school code</strong>, <strong>bus number ({driver.bus_number || 'n/a'})</strong> and this PIN to log in on the driver app.
          </p>

          <div style={{ marginBottom: 10 }}>
            <label style={label}>New PIN (4–8 digits)</label>
            <input
              style={{ ...input, fontSize: 22, letterSpacing: 8, textAlign: 'center' }}
              value={pin}
              onChange={e => setPin(e.target.value.replace(/\D/g, '').slice(0, 8))}
              inputMode="numeric"
              placeholder="••••"
              autoFocus
            />
          </div>

          {err && <div style={{ background: '#fee', color: '#b00', padding: 8, borderRadius: 6, fontSize: 12, marginBottom: 10 }}>{err}</div>}

          <div style={{ display: 'flex', justifyContent: 'space-between', gap: 8 }}>
            <button style={{ ...btn('danger'), visibility: driver.has_pin ? 'visible' : 'hidden' }} onClick={clearPin} disabled={busy}>Clear PIN</button>
            <div style={{ display: 'flex', gap: 8 }}>
              <button style={btn('secondary')} onClick={onClose} disabled={busy}>Cancel</button>
              <button style={btn('primary')} onClick={save} disabled={busy || !pin}>{busy ? 'Saving…' : 'Save PIN'}</button>
            </div>
          </div>
        </div>
      </div>
    </Modal>
  );
}

const modalBackdrop = {
  position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.5)',
  display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 9999,
};
const modalBox = {
  background: '#fff', borderRadius: 12, padding: 22,
  width: '92%', maxWidth: 420, boxShadow: '0 20px 60px rgba(0,0,0,0.3)',
};
